import { Stack, Typography } from "@mui/material";
import { WasteManifestSource } from "generated-graphql/graphql";
import { formatNumber } from "util/formatNumber";

import { FieldWarningIndicator } from "./FieldWarningIndicator";
import { SOURCE_ORDER } from "./manifestSourceDisplay";
import { SourcesDisagreeTooltip } from "./SourcesDisagreeTooltip";

export interface AmountCellProps {
  /** Per-source line-item amounts (lbs). Missing sources are omitted/null. */
  amounts: Partial<Record<WasteManifestSource, number | null | undefined>>;
  /**
   * Replaces the e-Manifest entry in the disagreement tooltip. Pass it when a
   * Box 18a override is set, so the tooltip shows Box 11 and the override.
   */
  eManifestTooltipText?: string | null;
  /** The e-Manifest amount is a confirmed Box 18a override; label it when it is the amount shown. */
  eManifestIsBox18aOverride?: boolean;
  /** Names the document source "Uploaded bill of lading" in the disagreement tooltip. */
  isBillOfLading?: boolean;
}

type SourceAmounts = Partial<Record<WasteManifestSource, number | null | undefined>>;

const fmt = (amount: number): string => `${formatNumber(amount)} lbs`;

/** Present (non-null) amounts in source-display order. */
function presentAmounts(amounts: SourceAmounts): number[] {
  return SOURCE_ORDER.flatMap((source) => {
    const amount = amounts[source];
    return amount != null ? [amount] : [];
  });
}

/** The largest present amount formatted as "N lbs", or undefined if none reported. */
export function largestAmountText(amounts: SourceAmounts): string | undefined {
  const present = presentAmounts(amounts);
  return present.length > 0 ? fmt(Math.max(...present)) : undefined;
}

/** Per-source amounts formatted as "N lbs" for the disagreement tooltip. */
export function formatAmountSourceValues(
  amounts: SourceAmounts
): Partial<Record<WasteManifestSource, string>> {
  const formatted: Partial<Record<WasteManifestSource, string>> = {};
  for (const source of SOURCE_ORDER) {
    const amount = amounts[source];
    if (amount != null) formatted[source] = fmt(amount);
  }
  return formatted;
}

/**
 * Collapsed Amount cell for the line-items grid: shows the largest amount across
 * the e-Manifest / Vendor / Document sources (ignoring missing values). When the
 * present sources disagree, a "sources disagree" warning icon sits to the right
 * of the value; hovering it reveals every source's amount.
 */
export function AmountCell({
  amounts,
  eManifestTooltipText,
  eManifestIsBox18aOverride = false,
  isBillOfLading = false,
}: AmountCellProps) {
  const present = presentAmounts(amounts);

  if (present.length === 0) {
    return null;
  }

  // Disagreement = more than one distinct present value (missing is not a disagreement).
  const hasDisagreement = new Set(present).size > 1;
  const shown = Math.max(...present);
  const showsBox18a = eManifestIsBox18aOverride && amounts[WasteManifestSource.EManifest] === shown;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {fmt(shown)}
      </Typography>
      {showsBox18a && (
        <Typography variant="caption" color="text.secondary" noWrap>
          Box 18a
        </Typography>
      )}
      {hasDisagreement && (
        <FieldWarningIndicator
          tooltip={
            <SourcesDisagreeTooltip
              isBillOfLading={isBillOfLading}
              values={{
                ...formatAmountSourceValues(amounts),
                ...(eManifestTooltipText
                  ? { [WasteManifestSource.EManifest]: eManifestTooltipText }
                  : {}),
              }}
            />
          }
          ariaLabel="Sources disagree on this value"
        />
      )}
    </Stack>
  );
}
